import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { SearchHistory, prepareSearchPrivacy, SEARCH_PRIVACY_READY } from '@theme/search-history';
import { searchQueryParts } from '@theme/search-query';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * Horizon predictive search with a query-only combobox. Product cards and dialog
 * actions remain ordinary links/buttons in the keyboard tab order.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement} searchError
 * @property {HTMLElement} searchStatus
 * @property {HTMLFormElement} form
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  /** @type {AbortController | null} */
  #activeFetch = null;
  #emptyMarkup = '';
  #isComposing = false;
  #emptyRevision = 0;
  #privacyReady;
  #history = new SearchHistory({
    locale: document.documentElement.lang,
    canPersist: () => window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true,
  });

  get dialog() {
    return this.closest('dialog-component');
  }

  get #isOpen() {
    const dialog = this.closest('dialog');
    return !dialog || (dialog.open && !dialog.classList.contains('dialog-closing'));
  }

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    if (!this.#emptyMarkup) this.#emptyMarkup = this.refs.predictiveSearchResults.innerHTML;
    this.#privacyReady = prepareSearchPrivacy();

    document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
    this.dialog?.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    this.dialog?.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
    window.addEventListener('resize', this.#updateOffset, { signal });
    window.visualViewport?.addEventListener('resize', this.#updateOffset, { signal });
    this.refs.searchInput.addEventListener('compositionstart', this.#handleCompositionStart, { signal });
    this.refs.searchInput.addEventListener('compositionend', this.search, { signal });
    this.refs.searchInput.addEventListener('blur', () => { this.#currentIndex = -1; }, { signal });
    this.refs.form.addEventListener('submit', this.#saveSubmittedSearch, { signal });
    document.addEventListener('visitorConsentCollected', this.#handleConsentChange, { signal });
    document.addEventListener(SEARCH_PRIVACY_READY, this.#handleConsentChange, { signal });
    window.addEventListener('storage', this.#handleStorageChange, { signal });
    this.addEventListener('click', this.#handleModalClick, { signal });
    this.#syncCombobox();
  }

  disconnectedCallback() {
    this.#isComposing = false;
    this.#cancelSearch();
    this.#controller.abort();
    super.disconnectedCallback();
  }

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  #handleModalClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const queryLink = event.target.closest('a[data-search-query]');
    if (queryLink && !event.defaultPrevented) this.#history.save(queryLink.dataset.searchQuery);
    if (!event.target.closest('button, a, input, label')) this.refs.searchInput.focus();
  };

  #saveSubmittedSearch = (event) => {
    if (!event.defaultPrevented) this.#history.save(this.refs.searchInput.value);
  };

  #handleConsentChange = () => {
    if (!this.#history.allowed()) this.#history.clear();
    if (this.#isOpen && !this.refs.searchInput.value.trim()) this.#restoreEmptyState();
  };

  #handleStorageChange = (event) => {
    if (event.key === this.#history.key || event.key === null) {
      this.#history.refresh();
      if (this.#isOpen && !this.refs.searchInput.value.trim()) this.#restoreEmptyState();
    }
  };

  #handleDialogOpen = () => {
    this.#updateOffset();
    this.refs.searchInput.value = '';
    this.refs.resetButton.hidden = true;
    this.#restoreEmptyState();
  };

  #handleDialogClose = () => {
    this.#isComposing = false;
    this.#cancelSearch();
    this.refs.searchInput.value = '';
    this.refs.resetButton.hidden = true;
    this.#renderEmptyState();
  };

  #updateOffset = () => {
    if (!this.#isOpen) return;
    const announcement = document.querySelector('#header-group .announcement-bar');
    const bounds = announcement?.getBoundingClientRect();
    const offset = bounds && bounds.top < window.innerHeight
      ? Math.max(0, Math.min(bounds.height, bounds.bottom))
      : 0;
    this.closest('dialog')?.style.setProperty('--lazy-oaf-search-offset', offset + 'px');
  };

  get #allResultsItems() {
    if (this.hasAttribute('data-search-loading')) return [];
    return Array.from(this.querySelectorAll('[role="listbox"] [role="option"]')).filter(
      (item) => item instanceof HTMLElement && item.getClientRects().length > 0 &&
        getComputedStyle(item).visibility !== 'hidden'
    );
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const input = this.refs.searchInput;
    for (const item of this.querySelectorAll('[role="option"]')) {
      item.setAttribute('aria-selected', 'false');
      item.classList.remove('keyboard-focus');
    }
    input.removeAttribute('aria-activedescendant');
    const active = this.#allResultsItems[index];
    if (!(active instanceof HTMLElement)) return;
    active.setAttribute('aria-selected', 'true');
    active.classList.add('keyboard-focus');
    input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }

  #syncCombobox() {
    const input = this.refs.searchInput;
    const list = this.querySelector('[role="listbox"]');
    const expanded = this.#isOpen && this.#allResultsItems.length > 0;
    input.setAttribute('aria-expanded', String(expanded));
    if (expanded && list?.id) input.setAttribute('aria-controls', list.id);
    else input.removeAttribute('aria-controls');
    this.#currentIndex = -1;
  }

  onSearchKeyDown = (event) => {
    if (event.target !== this.refs.searchInput || event.defaultPrevented) return;
    if (event.isComposing || this.#isComposing) {
      if (event.key === 'Escape') event.stopPropagation();
      return;
    }
    const items = this.#allResultsItems;
    const index = this.#currentIndex;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        if (!items.length) return;
        event.preventDefault();
        this.#currentIndex = event.key === 'ArrowDown'
          ? (index + 1) % items.length
          : (index <= 0 ? items.length : index) - 1;
        break;
      case 'Enter':
        if (index >= 0) {
          event.preventDefault();
          items[index].click();
        } else {
          // The native GET form preserves locale, query and options[prefix].
          this.#cancelSearch();
        }
        break;
      case 'Escape':
        this.#cancelSearch();
        break; // Horizon owns close and focus restoration.
      case 'Tab':
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Home':
      case 'End':
        this.#currentIndex = -1;
        break;
    }
  };

  #handleCompositionStart = () => {
    this.#isComposing = true;
    this.#cancelSearch();
  };

  search = (event) => {
    if (event?.type === 'compositionend') this.#isComposing = false;
    if (event?.isComposing || this.#isComposing) return;
    this.#cancelSearch();
    const query = this.refs.searchInput.value.trim();
    this.refs.resetButton.hidden = !query;
    if (!query) {
      this.#restoreEmptyState();
      return;
    }
    this.#setLoading(true);
    this.#debouncedSearch();
  };

  #debouncedSearch = debounce(() => {
    const query = this.refs.searchInput.value.trim();
    if (query && this.#isOpen && !this.#isComposing) this.#getSearchResults(query);
  }, 200);

  resetSearch = () => {
    this.#isComposing = false;
    this.#cancelSearch();
    this.refs.searchInput.value = '';
    this.refs.resetButton.hidden = true;
    this.#restoreEmptyState();
    this.refs.searchInput.focus();
  };

  retrySearch = () => {
    this.refs.searchInput.focus();
    this.search();
  };

  #cancelSearch() {
    this.#emptyRevision++;
    this.removeAttribute('data-history-pending');
    this.#debouncedSearch.cancel();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#setLoading(false);
    this.#setError(false);
    this.#currentIndex = -1;
  }

  #setLoading(loading) {
    this.toggleAttribute('data-search-loading', loading);
    this.refs.predictiveSearchResults.setAttribute('aria-busy', String(loading));
    if (this.refs.searchStatus) {
      this.refs.searchStatus.textContent = loading ? (this.dataset.loadingLabel ?? '') : '';
    }
    this.#syncCombobox();
  }

  #setError(failed) {
    this.toggleAttribute('data-search-error', failed);
    if (this.refs.searchError) this.refs.searchError.hidden = !failed;
    this.refs.predictiveSearchResults.hidden = failed;
    this.#syncCombobox();
  }

  #resetScrollPositions() {
    this.querySelector('.predictive-search-form__content-wrapper')?.scrollTo(0, 0);
  }

  #createAbortController() {
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    return controller;
  }

  async #getSearchResults(query) {
    if (!this.dataset.sectionId) {
      this.#setLoading(false);
      return;
    }
    const controller = this.#createAbortController();
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'query,product');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');

    const deferred = SearchUpdateEvent.createPromise();
    // Consumers can still observe rejection; cancellation/failure must not become
    // an unhandled rejection when no analytics subscriber is present.
    deferred.promise.catch(() => {});
    this.dispatchEvent(new SearchUpdateEvent({ search: { query }, promise: deferred.promise }));

    try {
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url, controller.signal);
      if (controller.signal.aborted || !this.isConnected || !this.#isOpen || query !== this.refs.searchInput.value.trim()) {
        deferred.reject(new DOMException('Search cancelled', 'AbortError'));
        return;
      }
      const results = new DOMParser().parseFromString(html, 'text/html').querySelector('#predictive-search-results');
      if (!results) throw new Error('Invalid predictive search response');
      for (const label of results.querySelectorAll('[data-search-query-text]')) {
        const fragment = document.createDocumentFragment();
        for (const part of searchQueryParts(label.textContent, query)) {
          if (part.matched) {
            const mark = document.createElement('mark');
            mark.textContent = part.text;
            fragment.append(mark);
          } else fragment.append(document.createTextNode(part.text));
        }
        label.replaceChildren(fragment);
      }
      morph(this.refs.predictiveSearchResults, results);
      this.#setLoading(false);
      this.#syncCombobox();
      this.#resetScrollPositions();
      const totalCount = this.querySelectorAll('[role="option"], .predictive-search-results__card--product').length;
      deferred.resolve({ totalCount });
    } catch (error) {
      deferred.reject(error);
      if (controller.signal.aborted || !this.isConnected || !this.#isOpen) return;
      this.#setLoading(false);
      this.#setError(true);
    }
  }

  #renderEmptyState() {
    const empty = new DOMParser().parseFromString(this.#emptyMarkup, 'text/html').querySelector('#predictive-search-results');
    if (empty) {
      const entries = this.#history.read();
      const recent = empty.querySelector('[data-recent-searches]');
      const template = this.querySelector('[data-recent-search-template]');
      if (entries.length && recent && template instanceof HTMLTemplateElement) {
        recent.hidden = false;
        const popular = empty.querySelector('.predictive-search-results__popular');
        if (popular) popular.hidden = true;
        for (const entry of entries) {
          const item = template.content.cloneNode(true);
          const link = item.querySelector('a');
          const remove = item.querySelector('button');
          const url = new URL(this.refs.form.action, location.origin);
          url.searchParams.set('q', entry.query);
          url.searchParams.set('options[prefix]', 'last');
          url.searchParams.set('type', 'product');
          link.href = url.toString();
          link.textContent = entry.query;
          link.dataset.searchQuery = entry.query;
          remove.dataset.searchQuery = entry.query;
          remove.setAttribute('aria-label', this.dataset.removeSearchLabel.replace('__query__', () => entry.query));
          recent.querySelector('ul').append(item);
        }
      }
      morph(this.refs.predictiveSearchResults, empty);
    }
    this.#setError(false);
    this.#syncCombobox();
    this.#resetScrollPositions();
  }

  async #restoreEmptyState() {
    this.#cancelSearch();
    const revision = this.#emptyRevision;
    if (!window.Shopify?.customerPrivacy) {
      this.setAttribute('data-history-pending', '');
      await this.#privacyReady;
    }
    if (revision !== this.#emptyRevision || !this.isConnected || !this.#isOpen || this.refs.searchInput.value.trim()) return;
    this.#renderEmptyState();
    this.removeAttribute('data-history-pending');
    this.#syncCombobox();
  }

  removeRecentSearch(event) {
    event.stopPropagation();
    const button = event.target.closest('button[data-search-query]');
    if (!button) return;
    const buttons = [...this.querySelectorAll('[data-recent-searches] button')];
    const index = buttons.indexOf(button);
    this.#history.remove(button.dataset.searchQuery);
    // Update this visit even if storage writes have become blocked.
    button.closest('li').remove();
    const remaining = [...this.querySelectorAll('[data-recent-searches] button')];
    if (remaining.length) remaining[Math.min(index, remaining.length - 1)].focus();
    else {
      this.querySelector('[data-recent-searches]').hidden = true;
      const popular = this.querySelector('.predictive-search-results__popular');
      if (popular) popular.hidden = false;
      this.refs.searchInput.focus();
    }
    this.#syncCombobox();
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
